import { applyOpacity, periverseTheme } from '@peritasai/ui-shared';
import type { SxProps, Theme } from '@mui/material/styles';

export const pageContainerSx = {
  px: { xs: '2.5rem', md: '1.5rem' },
  py: { xs: '2rem', md: '0.5rem' },
  display: 'flex',
  justifyContent: { xs: 'center', md: 'flex-start' },
  minHeight: 'calc(100vh - 80px)',
  position: 'relative',
} satisfies SxProps<Theme>;

export const contentWrapSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: { xs: 'center', md: 'flex-start' },
  flex: 1,
  height: { xs: '100%', md: 'calc(100vh - 165px)' },
  marginTop: { xs: '-36px', md: '0px' },
} satisfies SxProps<Theme>;

export const scrollableAreaSx = {
  scrollbarGutter: 'stable',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '15px',
    backgroundColor: '#CFD8DC',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
} satisfies SxProps<Theme>;

export const pageContainerVariantSx = (
  isDiscoveryStep: boolean,
  hasSelectedLocation = false
) =>
  ({
    ...pageContainerSx,
    paddingLeft: isDiscoveryStep
      ? { xs: '0px', md: '1rem', lg: '3rem', xl: '3rem' }
      : { xs: '0px', md: '1.5rem', lg: '3rem', xl: '3rem' },
    paddingRight: isDiscoveryStep
      ? { xs: '0px', md: '1rem', lg: '3rem', xl: '3rem' }
      : { xs: '0px', md: '1.5rem', lg: '3rem', xl: '3rem' },
    py: isDiscoveryStep
      ? { xs: '0px', md: '1rem', lg: '1rem' }
      : { xs: pageContainerSx.py.xs, md: '0px'},
    minHeight: isDiscoveryStep
      ? { xs: 'calc(100dvh - 95px)', md: 'calc(100vh - 150px)', lg: 'calc(100vh - 120px)' }
      : pageContainerSx.minHeight,
    justifyContent: isDiscoveryStep ? 'flex-start' : pageContainerSx.justifyContent,
    mt: hasSelectedLocation ? { xs: '0px', md: '0px', lg: '0px' } : '0px',
    alignItems: isDiscoveryStep
      ? 'stretch'
      : { xs: 'center', md: 'flex-start' },
    ...(isDiscoveryStep
      ? {
          height: {
            xs: 'calc(100dvh - 95px)',
            md: 'calc(100vh - 150px)',
            lg: 'calc(100vh - 120px)',
          },
        }
      : {}),
  }) satisfies SxProps<Theme>;

export const contentWrapVariantSx = (isDiscoveryStep: boolean) =>
  ({
    ...contentWrapSx,
    px: '0px',
    marginTop: isDiscoveryStep
      ? { xs: '0px', md: '0px' }
      : contentWrapSx.marginTop,
    height: isDiscoveryStep
      ? { xs: '100%', md: 'calc(100vh - 120px)', lg: 'calc(100vh - 120px)' }
      : contentWrapSx.height,
    minHeight: isDiscoveryStep
      ? { xs: '100%', md: 'calc(100vh - 120px)', lg: 'calc(100vh - 120px)' }
      : undefined,
    justifyContent: isDiscoveryStep ? 'flex-start' : contentWrapSx.justifyContent,
    alignItems: isDiscoveryStep
      ? 'stretch'
      : { xs: 'center', md: 'flex-start' },
  }) satisfies SxProps<Theme>;

export const cardSx = {
  width: { xs: '680px', md: '100%' },
  maxWidth: { xs: 680, md: '100%' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: { xs: 'center', md: 'stretch' },
  gap: { xs: '16px', md: '16px' },
  borderRadius: '24px',
  border: `1px solid ${periverseTheme.palette.divider}`,
  background: periverseTheme.palette.background.paper,
  boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.08)',
  padding: { xs: '40px 32px', md: '16px' },
} satisfies SxProps<Theme>;

export const listRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  maxWidth: '100%',
  padding: '12px 12px 12px 16px',
  gap: '16px',
  borderRadius: '16px',
  border: `1px solid ${periverseTheme.palette.divider}`,
  background: periverseTheme.palette.background.default,
} satisfies SxProps<Theme>;

export const cardTitleSx = {
  color: { xs: periverseTheme.palette.text.primary, md: 'rgba(0, 0, 0, 0.87)' },
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: { xs: '20px', md: '16px' },
  fontWeight: { xs: 500, md: 400 },
  lineHeight: { xs: '26.68px', md: '24px' },
  letterSpacing: '0.15px',
  textAlign: { xs: 'center', md: 'left' },
} satisfies SxProps<Theme>;

export const selectHubLocationTitleSx = {
  ...cardTitleSx,
  [periverseTheme.breakpoints.up('md')]: {
    color: 'rgba(0, 0, 0, 0.87)',
    fontFeatureSettings: '"liga" off, "clig" off',
    fontFamily: '"Helvetica Neue", "Roboto", "Arial", sans-serif',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '150%',
    letterSpacing: '0.15px',
  },
} satisfies SxProps<Theme>;

export const selectButtonSx = {
  display: 'flex',
  padding: '8px 22px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '8px',
  background: periverseTheme.palette.primary.main,
  boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.25)',
  color: periverseTheme.palette.primary.contrastText,
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const locationTitleSx = {
  textAlign: { xs: 'center', md: 'left' },
  alignSelf: { xs: 'stretch', md: 'auto' },
  flex: 1,
  color: { xs: periverseTheme.palette.text.primary, md: 'rgba(0, 0, 0, 0.60)' },
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: { xs: '24px', md: '16px' },
  fontFeatureSettings: '"liga" off, "clig" off',
  fontWeight: { xs: 500, md: 400 },
  lineHeight: { xs: '29.64px', md: '24px' },
  letterSpacing: { xs: '0.25px', md: '0.15px' },
} satisfies SxProps<Theme>;

export const selectedFlowHeaderTitleStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  flex: '1 1 0',
  minWidth: 0,
} satisfies SxProps<Theme>;

export const selectedFlowTitleSx = {
  color: 'rgba(0, 0, 0, 0.87)',
  fontFeatureSettings: '"liga" off, "clig" off',
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '32px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const selectedFlowLocationSx = {
  color: 'rgba(0, 0, 0, 0.60)',
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const featurePageTitleWrapSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  pl: { xs: '1.5rem', md: '0px' },
  pr: { xs: '1.5rem', md: '0px' },
} satisfies SxProps<Theme>;

export const featurePageStandaloneTitleWrapSx = {
  ...featurePageTitleWrapSx,
  position: { xs: 'absolute', md: 'static' },
  top: { xs: '24px', md: 'auto' },
  left: { xs: '0px', md: 'auto' },
  zIndex: { xs: 1, md: 'auto' },
  justifyContent: { xs: 'flex-start', md: 'center' },
  minHeight: { xs: 'auto', md: '72px' },
} satisfies SxProps<Theme>;

export const featurePageTitleCompactWrapSx = {
  ...featurePageTitleWrapSx,
  pt: { xs: '0px', md: '16px' },
  pb: { xs: '0px', md: '12px' },
  minHeight: { xs: 'auto', md: '44px' },
} satisfies SxProps<Theme>;

export const featurePageTitleTextSx = {
  color: 'rgba(0, 0, 0, 0.87)',
  fontFeatureSettings: '"liga" off, "clig" off',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '22px',
  letterSpacing: '0.25px',
} satisfies SxProps<Theme>;

export const locationHeaderRowSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: { xs: 'center', md: 'flex-end' },
  gap: '16px',
} satisfies SxProps<Theme>;

export const caseListSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: { xs: 'center', md: 'stretch' },
  gap: '16px',
  width: '100%',
} satisfies SxProps<Theme>;

export const caseListCardStackSx = (hasSelectedLocation: boolean) =>
  ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: { xs: 'center', md: 'stretch' },
    gap: hasSelectedLocation ? { xs: '32px', md: '32px', lg: '12px' } : '32px',
    pt: hasSelectedLocation ? { xs: '5px', md: '5px', lg: '0px' } : '1rem',
    width: '100%',
  }) satisfies SxProps<Theme>;

export const caseListScrollSx = (showAll: boolean, listHeight: string) =>
  ({
    ...caseListSx,
    height: listHeight,
    overflowY: showAll ? 'scroll' : 'auto',
    overflowX: 'hidden',
    gap: { xs: '16px', md: '12px' },
    pr: '5px',
    pb: showAll ? 0 : undefined,
    ...scrollableAreaSx,
  }) satisfies SxProps<Theme>;

export const caseListRowWithGapSx = {
  ...listRowSx,
  gap: 2,
} satisfies SxProps<Theme>;

export const caseListRowDetailsSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
} satisfies SxProps<Theme>;

export const changeLocationButtonSx = {
  height: '40px',
  padding: '6px 10px',
  borderRadius: '8px',
  color: periverseTheme.palette.secondary.main,
  textTransform: 'capitalize',
  zIndex: 2,
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const cancelSelectionButtonSx = {
  height: '40px',
  padding: '6px 22px',
  borderRadius: '8px',
  borderColor: applyOpacity(periverseTheme.palette.primary.light, 0.5),
  color: periverseTheme.palette.primary.main,
  textTransform: 'capitalize',
  zIndex: 2,
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const caseCardWrapSx = {
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: { xs: 'center', md: 'stretch' },
  gap: '32px',
  padding: '0px',
  flex: '1 0 0',
  alignSelf: 'stretch',
  justifyContent: { xs: 'center', md: 'flex-start' },
} satisfies SxProps<Theme>;

export const selectCaseHeadingSx = {
  width: '100%',
  display: 'flex',
  justifyContent: { xs: 'center', md: 'flex-start' },
} satisfies SxProps<Theme>;

export const locationHeadingSx = {
  width: '100%',
  display: 'flex',
  justifyContent: { xs: 'center', md: 'flex-start' },
} satisfies SxProps<Theme>;

export const caseTitleSx = {
  color: periverseTheme.palette.text.primary,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const caseSubtitleSx = {
  color: periverseTheme.palette.text.secondary,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20.02px',
  letterSpacing: '0.17px',
} satisfies SxProps<Theme>;

export const discoveryContainerSx = {
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: { xs: 'center', md: 'stretch' },
  flex: 1,
  height: '100%',
  minHeight: 0,
  alignSelf: 'stretch',
} satisfies SxProps<Theme>;

export const discoveryContentWrapSx = {
  flex: 1,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'stretch',
} satisfies SxProps<Theme>;

export const discoveryHeaderWrapSx = {
  width: '100%',
  maxWidth: '100%',
  px: { xs: '16px', md: "0px" },
  pt: { xs: '16px', md: '0px' },
} satisfies SxProps<Theme>;

export const discoveryHeaderTitleStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: '1 1 0',
} satisfies SxProps<Theme>;

export const discoveryHeaderSkeletonStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: '1 1 0',
} satisfies SxProps<Theme>;

export const discoveryHeaderSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  gap: '4px',
} satisfies SxProps<Theme>;

export const discoveryCardWrapSx = {
  width: '100%',
  maxWidth: '100%',
  flex: 1,
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  px: { xs: '16px', md: "0px" },
  pt: "0px",
} satisfies SxProps<Theme>;

export const discoveryTitleSx = {
  color: periverseTheme.palette.text.primary,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '20px',
  fontWeight: 500,
  lineHeight: '32px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const discoverySubtitleSx = {
  color: periverseTheme.palette.text.secondary,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const discoveryActionsSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
} satisfies SxProps<Theme>;

export const discoveryCancelButtonSx = {
  height: '56px',
  padding: '8px 22px',
  borderRadius: '8px',
  borderColor: applyOpacity(periverseTheme.palette.primary.light, 0.5),
  color: periverseTheme.palette.primary.main,
  textTransform: 'capitalize',
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const discoveryAssignButtonSx = {
  height: '56px',
  padding: '8px 22px',
  borderRadius: '8px',
  textTransform: 'capitalize',
  ...periverseTheme.typography.buttonLarge,
} satisfies SxProps<Theme>;

export const discoveryCardSx = {
  width: '100%',
  maxWidth: '100%',
  alignSelf: 'stretch',
  borderRadius: '24px',
  border: `1px solid ${periverseTheme.palette.divider}`,
  background: periverseTheme.palette.background.paper,
  boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.08)',
  padding: { xs: '16px', md: '24px' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '0px',
  flex: 1,
  minHeight: 0,
  height: '100%',
} satisfies SxProps<Theme>;

export const discoveryPanelSx = {
  width: '100%',
  alignSelf: 'stretch',
  flex: '1 0 0',
  borderRadius: '16px',
  border: `1px solid ${periverseTheme.palette.primary.main}`,
  background: '#E1F7FE',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '48px',
  padding: '0px',
  boxSizing: 'border-box',
  minHeight: 0,
} satisfies SxProps<Theme>;

export const discoveryPanelLoadingSx = {
  ...discoveryPanelSx,
  gap: '24px',
} satisfies SxProps<Theme>;

export const discoveryCartIllustrationSx = {
  display: 'flex',
  width: '342px',
  height: '269.835px',
  padding: '1.734px 0.926px 0.055px 0',
  justifyContent: 'center',
  alignItems: 'center',
  aspectRatio: '109 / 86',
  objectFit: 'contain',
} satisfies SxProps<Theme>;

export const discoveryCartIllustrationLoadingSx = {
  ...discoveryCartIllustrationSx,
  opacity: 0.6,
} satisfies SxProps<Theme>;

export const discoveryWaitingCopySx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
} satisfies SxProps<Theme>;

export const discoveryDetectedWrapSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  flex: 1,
  minHeight: 0,
} satisfies SxProps<Theme>;

export const discoveryDetectedListSx = {
  display: 'grid',
  gap: 1.5,
  width: '100%',
  maxHeight: {xs:'calc(100vh - 400px)', md:'calc(100% - 165px)'},
  overflowY: 'auto',
  pr: '4px',
  ...scrollableAreaSx,
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  padding: '12px 12px 12px 16px',
  minHeight: '72px',
  maxHeight: '72px',
  overflow: 'hidden',
  borderRadius: '16px',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  backgroundColor: '#F7F9FA',
  cursor: 'pointer',
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayDetailsSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap:'4px'
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '8px',
  backgroundColor: '#E1F7FE',
  px: '8px',
  py: '2px',
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayCheckWrapSx = {
  margin: '0 16px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  minWidth: '120px',
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayStatusTextSx = {
  color: '#2E7D32',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: '0.1px',
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayNoopTextSx = {
  color: 'rgba(0, 0, 0, 0.6)',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: '0.1px',
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayFailedTextSx = {
  color: '#D32F2F',
  fontFamily: 'var(--fontFamily, "Helvetica Neue")',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: '0.1px',
} satisfies SxProps<Theme>;

export const discoveryDetectedTrayUncheckedSx = {
  width: '21px',
  height: '21px',
  borderRadius: '4px',
  border: `1.5px solid ${periverseTheme.palette.primary.main}`,
  backgroundColor: 'transparent',
} satisfies SxProps<Theme>;

export const discoverySuccessPanelSx = {
  borderRadius: '16px',
  border: '1px solid #9CCC9C',
  backgroundColor: '#E9F6E9',
  minHeight: 360,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '48px',
  width: '100%',
  flex: 1,
  height: '100%',
} satisfies SxProps<Theme>;

export const discoverySuccessIconWrapSx = {
  width: '54px',
  height: '54px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
} satisfies SxProps<Theme>;

export const discoveryStatusTitleSx = {
  textAlign: 'center',
  color: 'rgba(0, 0, 0, 0.87)',
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '20px',
  fontWeight: 500,
  lineHeight: '26.68px',
  fontFeatureSettings: '"liga" off, "clig" off',
} satisfies SxProps<Theme>;

export const discoveryDetectedTitleSx = {
  ...discoveryStatusTitleSx,
  mt: '24px',
  mb: '16px',
  [periverseTheme.breakpoints.up('md')]: {
    mt: 0,
    mb: 0,
    textAlign: 'left',
    color: 'rgba(0, 0, 0, 0.87)',
    fontFamily: periverseTheme.typography.fontFamily,
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
} satisfies SxProps<Theme>;

export const discoveryStatusRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
} satisfies SxProps<Theme>;

export const discoveryStatusLabelSx = {
  color: periverseTheme.palette.primary.main,
  fontFamily: periverseTheme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: '0.15px',
} satisfies SxProps<Theme>;

export const errorCardContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  gap: '16px',
} satisfies SxProps<Theme>;

export const errorCardActionsSx = {
  display: 'flex',
  justifyContent: 'center',
  gap: 1,
} satisfies SxProps<Theme>;

export const loadingCardGridSx = {
  display: 'grid',
  gap: 2,
} satisfies SxProps<Theme>;

export const locationListRowContentSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
} satisfies SxProps<Theme>;

export const hubStatusHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '16px',
  px: '24px',
  py: '16px',
  width: '100%',
} satisfies SxProps<Theme>;

export const hubStatusIconWrapSx = {
  width: '40px',
  height: '40px',
  borderRadius: '20px',
  backgroundColor: '#FFF2E0',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
} satisfies SxProps<Theme>;

export const hubStatusBodySx = {
  px: '24px',
  py: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'flex-start',
  width: '100%',
} satisfies SxProps<Theme>;

export const hubStatusFooterSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  px: '24px',
  pb: '24px',
  width: '100%',
} satisfies SxProps<Theme>;

export const popupOverlaySx = {
  position: 'fixed',
  inset: 0,
  width: '100vw',
  height: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: { xs: '16px', md: '24px' },
  py: { xs: '24px', md: '32px' },
  backgroundColor: 'rgba(0, 0, 0, 0.20)',
  zIndex: periverseTheme.zIndex.modal,
} satisfies SxProps<Theme>;
